import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTasks,
  faListCheck,
} from '@fortawesome/free-solid-svg-icons';
import './MatterTasksPage.css';
import TasksTopBar from './MatterTasks/Components/TopBar/TopBar';
import TasksTable from './MatterTasks/Components/TaskTable/TasksTable';
import ChecklistTable from './MatterChecklist/Components/ChecklistTable/ChecklistTable';
import ProjectChecklistTable from '../ProjectTasks/ProjectChecklist/Components/ChecklistTable/ChecklistTable';
import DataBar from './MatterTasks/Components/DataBar/DataBar';
import TasksFilterBar from './MatterTasks/Components/TasksFilterBar/TasksFilterBar';
import TaskService from '../../../AppSettings/services/TaskService';
import ChecklistService from '../../../AppSettings/services/ChecklistService';
import EmployeeService from '../../../AppSettings/services/EmployeeService';
import { TaskByEmployee } from '../../../Models/Tasks/TaskModels';
import { ChecklistItem } from '../../../Models/Checklist/ChecklistModels';
import { Employee } from '../../../Models/Employee/EmployeeModels';
import useActiveEmployeeId from '../../../AppSettings/hooks/useActiveEmployeeId';
import { isEngineeringEnvironment } from '../../../AppSettings/environmentConfig';

// Define types for column mappings
type TaskColumns = {
  [key: string]: keyof TaskByEmployee;
};

type ChecklistColumns = {
  [key: string]: keyof ChecklistItem;
};

type ColumnMapping = TaskColumns | ChecklistColumns;

type TableType = 'Tasks' | 'Checklist';

// Define pagination props type
interface PaginationProps {
  currPage: number;
  onPageChange: (page: number) => void;
  pageLimit: number;
  pageSize: number;
  onPageSizeChange: (size: number) => void;
  totalItems?: number; // Total number of items for "Showing X-Y of Z" display
  itemLabel?: string; // Label for items (e.g., "matters", "items")
}

// Define columns for each type of table
const taskColumns: TaskColumns = {
  Task: 'task_title',
  Status: 'status',
  Type: 'task_type',
  'Assigned To': 'assignedto',
  'Due Date': 'duedate',
};

const checklistColumns: ChecklistColumns = {
  Description: 'description',
  'Matter ID': 'matterid',
};

const MatterTasksPage = () => {
  const { matterId } = useParams<{ matterId: string }>();
  
  // Get the current user's information
  const employeeId = useActiveEmployeeId();

  // State management for data
  const [tasks, setTasks] = useState<TaskByEmployee[]>([]);
  const [checklistItems, setChecklistItems] = useState<ChecklistItem[]>([]);

  // State management for filtered data
  const [filteredTasks, setFilteredTasks] = useState<TaskByEmployee[]>([]);
  const [filteredChecklistItems, setFilteredChecklistItems] = useState<
    ChecklistItem[]
  >([]);

  // State management for pagination
  const [page, setPage] = useState<number>(1);
  const [pageLimit, setPageLimit] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10); // Items per page

  // State management for views and filters
  const [showMyTasks, setShowMyTasks] = useState<boolean>(true); // Toggle between "My Tasks" and "All Tasks" view
  // Set default active table based on environment: Checklist for engineering, Tasks for legal
  const [activeTable, setActiveTable] = useState<TableType>(
    isEngineeringEnvironment ? 'Checklist' : 'Tasks'
  );
  const [createTaskModalOpen, setCreateTaskModalOpen] = useState<boolean>(false);
  const [statusFilter, setStatusFilter] = useState<string | null>(null); // Status filter state
  const [matterFilter, setMatterFilter] = useState<string>('all'); // Matter (project) filter
  const [scopeFilter, setScopeFilter] = useState<string>('all'); // Internal/External filter (task_type)
  const [priorityFilter, setPriorityFilter] = useState<string>('all'); // Priority filter
  const [searchTerm, setSearchTerm] = useState<string>(''); // Task search term
  const [searchColumn, setSearchColumn] = useState<string>('Task'); // Column the search targets
  const [currentEmployee, setCurrentEmployee] = useState<Employee | null>(null);

  // Fetch tasks data based on the current view (My Tasks or All Tasks)
  const fetchTasks = useCallback(async (): Promise<void> => {
    if (!matterId) return;
    
    try {
      let data: TaskByEmployee[];
      if (showMyTasks && employeeId) {
        data = await TaskService.getTasksByMatterAndEmployee(
          matterId,
          employeeId
        );
      } else {
        data = await TaskService.getTasksByMatterId(matterId);
      }
      setTasks(data);
      setFilteredTasks(data);
    } catch (error) {
      console.error('Error fetching tasks:', error);
    }
  }, [matterId, showMyTasks, employeeId]);

  // Fetch all checklist items for this matter
  const fetchChecklistItems = useCallback(async (): Promise<void> => {
    if (!matterId) return;
    
    try {
      const data = await ChecklistService.getChecklistItemsByMatterId(
        matterId
      );
      setChecklistItems(data);
      setFilteredChecklistItems(data);
    } catch (error) {
      console.error('Error fetching checklist items:', error);
    }
  }, [matterId]);

  useEffect(() => {
    if (employeeId) {
      fetchTasks();
    }
    if (activeTable === 'Checklist') {
      fetchChecklistItems();
    }
  }, [
    employeeId,
    showMyTasks,
    activeTable,
    fetchTasks,
    fetchChecklistItems,
  ]);

  useEffect(() => {
    const fetchEmployeeDetails = async () => {
      if (employeeId) {
        try {
          const employeeData = await EmployeeService.getEmployeeById(
            employeeId
          );
          console.log('Fetched employee data:', employeeData);
          // Handle both array and single object responses from the API
          const employee = Array.isArray(employeeData) ? employeeData[0] : employeeData;
          console.log('Processed employee:', employee);
          setCurrentEmployee(employee || null);
        } catch (error) {
          console.error('Error fetching employee details:', error);
        }
      }
    };
    fetchEmployeeDetails();
  }, [employeeId]);

  // Update filtered data and pagination when data or filters change
  useEffect(() => {
    const updateFilteredData = () => {
      if (activeTable === 'Tasks' && tasks) {
        let filteredData = [...tasks];

        // Apply status filter: null = default (Pending + In Progress only), 'Total' = show all, else filter by status
        if (statusFilter === null) {
          filteredData = filteredData.filter(
            (task) => task.status === 'Pending' || task.status === 'In Progress'
          );
        } else if (statusFilter !== 'Total') {
          if (statusFilter === 'Overdue') {
            // Filter for overdue tasks (due date in past and not completed)
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            filteredData = filteredData.filter((task) => {
              if (!task.duedate || task.status === 'Completed') return false;
              const dueDate = new Date(task.duedate);
              dueDate.setHours(0, 0, 0, 0);
              return dueDate < today;
            });
          } else {
            filteredData = filteredData.filter(
              (task) => task.status === statusFilter
            );
          }
        }

        // Apply dropdown filters. 'all' means no constraint for that filter.
        if (matterFilter !== 'all') {
          filteredData = filteredData.filter(
            (task) => task.mattername === matterFilter
          );
        }
        if (scopeFilter !== 'all') {
          filteredData = filteredData.filter(
            (task) => (task.task_type || '').toLowerCase() === scopeFilter.toLowerCase()
          );
        }
        if (priorityFilter !== 'all') {
          filteredData = filteredData.filter(
            (task) => (task.priority || '').toLowerCase() === priorityFilter.toLowerCase()
          );
        }

        // Apply free-text search against the selected column (defaults to task title).
        if (searchTerm.trim()) {
          const term = searchTerm.trim().toLowerCase();
          const field = taskColumns[searchColumn] || 'task_title';
          filteredData = filteredData.filter((task) => {
            const value = task[field as keyof TaskByEmployee];
            return value?.toString().toLowerCase().includes(term);
          });
        }

        setFilteredTasks(filteredData);
        setPageLimit(Math.max(1, Math.ceil(filteredData.length / pageSize)));
      } else if (activeTable === 'Checklist' && checklistItems) {
        setFilteredChecklistItems(checklistItems);
        setPageLimit(Math.max(1, Math.ceil(checklistItems.length / pageSize)));
      }
    };

    updateFilteredData();
  }, [
    tasks,
    checklistItems,
    pageSize,
    statusFilter,
    activeTable,
    matterFilter,
    scopeFilter,
    priorityFilter,
    searchTerm,
    searchColumn,
  ]);

  // Handle status filter changes: toggle same card = back to default (null). Total = show all.
  const handleStatusFilterChange = (status: string): void => {
    setStatusFilter(statusFilter === status ? null : status);
    setPage(1); // Reset to first page when changing status filter
  };

  // Handle dropdown filter changes (matter, scope, priority). Reset to first page.
  const handleMatterFilterChange = (value: string): void => {
    setMatterFilter(value);
    setPage(1);
  };

  const handleScopeFilterChange = (value: string): void => {
    setScopeFilter(value);
    setPage(1);
  };

  const handlePriorityFilterChange = (value: string): void => {
    setPriorityFilter(value);
    setPage(1);
  };

  // Handle search functionality across different columns.
  // Tasks route through searchTerm state so the centralized filter effect applies
  // status + dropdown filters together; Checklist filters directly here.
  const handleSearch = (term: string, column?: string | null): void => {
    if (activeTable === 'Tasks') {
      setSearchTerm(term || '');
      setSearchColumn(column || 'Task');
      setPage(1);
      return;
    }

    if (!term || !column) {
      if (activeTable === 'Checklist') {
        setFilteredChecklistItems(checklistItems);
        setPageLimit(Math.max(1, Math.ceil(checklistItems.length / pageSize)));
      }
      setPage(1);
      return;
    }

    // Filter data based on the active table and columns for that table
    if (activeTable === 'Checklist') {
      const filtered = checklistItems.filter((entry) => {
        const value = entry[checklistColumns[column] as keyof ChecklistItem];
        return value?.toString().toLowerCase().includes(term.toLowerCase());
      });
      setFilteredChecklistItems(filtered);
      setPageLimit(Math.max(1, Math.ceil(filtered.length / pageSize)));
    }
    setPage(1); // Reset to page 1 after search
  };

  // Handle pagination changes
  const handlePagination = (page: number): void => setPage(page);

  // Handle page size changes
  const handlePageSizeChange = (newPageSize: number): void => {
    setPageSize(newPageSize);
    setPage(1); // Reset to first page when changing page size
  };

  // Toggle between "My Tasks" and "All Tasks" view
  const handleToggleTasks = (showMyTasks: boolean): void => {
    setShowMyTasks(showMyTasks);
    setPage(1); // Reset page to 1 when toggling task visibility
  };

  // Handle table change
  const handleTableChange = (table: TableType): void => {
    setActiveTable(table);
    setPage(1); // Reset to first page when changing table
    setStatusFilter(null); // Clear status filter when changing table
    setMatterFilter('all'); // Clear dropdown filters when changing table
    setScopeFilter('all');
    setPriorityFilter('all');
    setSearchTerm('');
    setSearchColumn('Task');
  };

  // Get current columns based on active table
  const getCurrentColumns = (): ColumnMapping => {
    switch (activeTable) {
      case 'Tasks':
        return taskColumns;
      case 'Checklist':
        return checklistColumns;
      default:
        return taskColumns;
    }
  };

  // Get current data based on active table
  const getCurrentData = (): TaskByEmployee[] | ChecklistItem[] => {
    switch (activeTable) {
      case 'Tasks':
        return filteredTasks;
      case 'Checklist':
        return filteredChecklistItems;
      default:
        return [];
    }
  };

  // Render appropriate table based on active table selection
  const renderTable = (): JSX.Element | null => {
    const currentData = getCurrentData();
    const paginatedData = currentData.slice(
      (page - 1) * pageSize,
      page * pageSize
    );

    const getItemLabel = () => {
      switch (activeTable) {
        case 'Tasks':
          return 'tasks';
        case 'Checklist':
          return 'checklist items';
        default:
          return 'items';
      }
    };

    const pagination: PaginationProps = {
      currPage: page,
      onPageChange: handlePagination,
      pageLimit: pageLimit,
      pageSize: pageSize,
      onPageSizeChange: handlePageSizeChange,
      totalItems: currentData.length,
      itemLabel: getItemLabel(),
    };

    switch (activeTable) {
      case 'Tasks':
        return (
          <TasksTable
            data={paginatedData as TaskByEmployee[]}
            pageSize={pageSize}
            pagination={pagination}
            onTaskDeleted={fetchTasks}
            createTaskModalOpen={createTaskModalOpen}
            onCloseCreateModal={() => {
              setCreateTaskModalOpen(false);
              fetchTasks();
            }}
            onOpenCreateModal={() => setCreateTaskModalOpen(true)}
          />
        );
      case 'Checklist':
        // Use custom ProjectChecklist table for engineering environment, regular table for legal
        if (isEngineeringEnvironment) {
          return <ProjectChecklistTable />;
        }
        return (
          <ChecklistTable
            data={paginatedData as ChecklistItem[]}
            pageSize={pageSize}
            pagination={pagination}
            onChecklistDeleted={fetchChecklistItems}
          />
        );
      default:
        return null;
    }
  };

  // Tab definitions. Engineering: Checklist, Tasks | Legal: Tasks, Checklist
  const tasksTab = {
    table: 'Tasks' as const,
    icon: faTasks,
    count: filteredTasks.length,
  };
  const checklistTab = {
    table: 'Checklist' as const,
    icon: faListCheck,
    count: filteredChecklistItems.length,
  };
  const tabs = isEngineeringEnvironment
    ? [checklistTab, tasksTab]
    : [tasksTab, checklistTab];

  // Main render
  return (
    <div className="matter-tasks-page-container">
      {/* Tab Navigation */}
      <div className="matter-tasks-tabs">
        {tabs.map(({ table, icon, count }) => (
          <button
            key={table}
            className={`matter-tasks-tab ${activeTable === table ? 'active' : ''}`}
            onClick={() => handleTableChange(table)}
          >
            <span className="matter-tasks-tab-content">
              <span className="matter-tasks-tab-icon">
                <FontAwesomeIcon icon={icon} />
              </span>
              <span>{table}</span>
            </span>
            {count > 0 && (
              <span className="matter-tasks-tab-count">{count}</span>
            )}
          </button>
        ))}
      </div>

      {/* TopBar with search and toggle - only show for Tasks */}
      {activeTable === 'Tasks' && (
        <>
          <TasksTopBar
            onSearch={handleSearch}
            onToggleTasks={handleToggleTasks}
            activeTable={activeTable}
            columns={getCurrentColumns()}
            permishId={currentEmployee?.permish_id ?? null}
          />
          {/* Filter Bar with toggle, filters, and search */}
          <TasksFilterBar
            showMyTasks={showMyTasks}
            onToggleTasks={handleToggleTasks}
            totalTasks={tasks.length}
            filteredTasks={filteredTasks.length}
            tasks={tasks}
            matterFilter={matterFilter}
            scopeFilter={scopeFilter}
            priorityFilter={priorityFilter}
            onMatterFilterChange={handleMatterFilterChange}
            onScopeFilterChange={handleScopeFilterChange}
            onPriorityFilterChange={handlePriorityFilterChange}
            onSearch={(term) => handleSearch(term, 'Task')}
            onAddTask={() => setCreateTaskModalOpen(true)}
          />
          {/* DataBar with status filter */}
          <DataBar
            setActiveTable={handleTableChange}
            activeTable={activeTable}
            onStatusFilter={handleStatusFilterChange}
            tasks={tasks}
          />
        </>
      )}

      {/* Table showing current page of data with integrated pagination */}
      {renderTable()}
    </div>
  );
};

export default MatterTasksPage;
